import { Injectable } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { IhLabResult } from '../../../../models/IhLabResult.model';
import { SampleCensorType, SampleCensorSign } from '../../../../enums/sampleCensorType.enum';
import { UnitSymbols, Units } from '../../../../enums/units.enum';
import { SampleTypeEnum } from '../../../../enums/sampleType.enum';
import { ValueConversionService } from '../../../../services/value-conversion.service';
import { Sample } from '../../../../models/sample.model';
import { sanitizeCssColor } from '../../../../shared/utils/css-color.util';
import {
  JurisdictionData,
  GroupedAgentData,
  JurisdictionGroup,
  RiskLevel
} from './gravimetric-widget.component';

export interface GravimetricPresentationLabels {
  duration: string;
  analyte: string;
  dosePercent: string;
  shiftAdjustedDosePercent: string;
  percentageOfOel: string;
  dbaTwa: string;
  jurisdiction: string;
  oel: string;
  dba: string;
  riskLegendDose: string;
  riskLegendOel: string;
  loqDisplay: string;
  shiftAdjustedLabel: string;
}

export interface GravimetricJurisdictionView extends JurisdictionData {
  percentageLabel: string;
  percentageDisplay: string;
  twaDisplay: string;
  twaUnit: string;
  oelLineDisplay: string;
  loqDisplayText: string;
}

export interface GravimetricAgentView extends GroupedAgentData {
  jurisdictions: GravimetricJurisdictionView[];
  agentNameUpper: string;
  exposureLevel: string;
}

export interface GravimetricGroupView extends JurisdictionGroup {
  agents: GravimetricAgentView[];
  legendTitle: string;
  jurisdictionLabel: string;
  hasLegend: boolean;
}

export interface GravimetricPresentation {
  groups: GravimetricGroupView[];
  isNoiseSample: boolean;
  duration: string;
  durationLabel: string;
  jurisdictionLabel: string;
  hasResults: boolean;
}

const DEFAULT_EXPOSURE_COLOR = '#828282';
const LOQ_GRAY_COLOR = '#9E9E9E';

@Injectable({
  providedIn: 'root'
})
export class GravimetricPresentationService {
  constructor(
    private valueConversionService: ValueConversionService,
    private decimalPipe: DecimalPipe
  ) { }

  build(
    sample: Sample | null | undefined,
    tenantRiskLevels: RiskLevel[],
    jurisdictionRiskLevelsMap: Map<string, RiskLevel[]>,
    labels: GravimetricPresentationLabels
  ): GravimetricPresentation {
    const ihLabResults = sample?.ihLabResults || [];
    const isNoiseSample = sample?.sampleType === SampleTypeEnum.Noise;

    if (!ihLabResults.length) {
      return {
        groups: [],
        isNoiseSample,
        duration: '',
        durationLabel: labels.duration,
        jurisdictionLabel: labels.jurisdiction,
        hasResults: false
      };
    }

    const duration = this.getDuration(sample);
    const groups = this.buildJurisdictionGroups(ihLabResults, isNoiseSample, tenantRiskLevels, jurisdictionRiskLevelsMap, labels);

    return {
      groups,
      isNoiseSample,
      duration,
      durationLabel: labels.duration,
      jurisdictionLabel: labels.jurisdiction,
      hasResults: groups.length > 0
    };
  }

  private buildJurisdictionGroups(
    ihLabResults: IhLabResult[],
    isNoiseSample: boolean,
    tenantRiskLevels: RiskLevel[],
    jurisdictionRiskLevelsMap: Map<string, RiskLevel[]>,
    labels: GravimetricPresentationLabels
  ): GravimetricGroupView[] {
    const jurisdictionMap = new Map<string, IhLabResult[]>();
    ihLabResults.forEach(r => {
      const key = r.jurisdictionId ?? 'none';
      if (!jurisdictionMap.has(key)) {
        jurisdictionMap.set(key, []);
      }
      jurisdictionMap.get(key)!.push(r);
    });

    return Array.from(jurisdictionMap.entries()).map(([jKey, results]) => {
      const jurisdictionId = jKey === 'none' ? undefined : jKey;
      const jurisdictionName = results[0]?.jurisdictionName || 'No Jurisdiction';
      const riskLevels = this.getRiskLevelsForJurisdiction(jurisdictionId, tenantRiskLevels, jurisdictionRiskLevelsMap);

      const agentMap = new Map<string, IhLabResult[]>();
      results.forEach(r => {
        if (!agentMap.has(r.agentId)) {
          agentMap.set(r.agentId, []);
        }
        agentMap.get(r.agentId)!.push(r);
      });

      const agents: GravimetricAgentView[] = Array.from(agentMap.entries()).map(([agentId, agentResults]) => {
        const firstResult = agentResults[0];
        const agentUnits = String(firstResult.agentUnits || UnitSymbols[Units.MgPerM3]);
        const jurisdictions = agentResults.map(r =>
          this.processJurisdiction(r, isNoiseSample, agentUnits, tenantRiskLevels, jurisdictionRiskLevelsMap, labels)
        );
        const allLoq = jurisdictions.every(j => j.isLoq);
        const maxExposurePercentage = Math.max(...jurisdictions.map(j => j.exposurePercentage));
        const agentName = firstResult.agentName || 'Unknown Agent';

        return {
          agentId,
          agentName,
          agentNameUpper: (labels.analyte || '').toUpperCase(),
          agentUnits,
          jurisdictions,
          maxExposurePercentage,
          maxExposureColor: allLoq ? LOQ_GRAY_COLOR : this.getExposureColor(maxExposurePercentage, jurisdictionId, tenantRiskLevels, jurisdictionRiskLevelsMap),
          exposureLevel: this.getExposureLevel(maxExposurePercentage)
        };
      });

      const legendItems = this.buildLegendFromRiskLevels(riskLevels);

      return {
        jurisdictionId,
        jurisdictionName,
        agents,
        legendItems,
        legendTitle: isNoiseSample ? labels.riskLegendDose : labels.riskLegendOel,
        jurisdictionLabel: labels.jurisdiction,
        hasLegend: legendItems.length > 0
      };
    });
  }

  private processJurisdiction(
    result: IhLabResult,
    isNoiseSample: boolean,
    agentUnits: string,
    tenantRiskLevels: RiskLevel[],
    jurisdictionRiskLevelsMap: Map<string, RiskLevel[]>,
    labels: GravimetricPresentationLabels
  ): GravimetricJurisdictionView {
    const isLoq = result.sampleCensor === SampleCensorType.LessThan;
    const effectiveOel = this.getEffectiveOel(result);
    const oelLabel = this.getOelLabel(result, labels);
    const rawDosePercentage = result.dosePercentage ?? null;
    const shiftAdjustedDosePercentage = result.correctedDosePercent ?? result.dosePercentage ?? null;
    const percentageLabel = isNoiseSample ? labels.shiftAdjustedDosePercent : labels.percentageOfOel;
    const twaUnit = isNoiseSample ? labels.dbaTwa : this.getUnitSymbol(agentUnits);
    const oelUnit = isNoiseSample ? labels.dba : this.getUnitSymbol(agentUnits);
    const convertedTwa = this.getConvertedTwa(result);
    const convertedOel = this.getConvertedOel(result);
    const effectiveOelValue = isNoiseSample
      ? (result.correctedOEL ?? result.oel ?? 0)
      : convertedOel;

    if (isLoq) {
      return {
        ihLabResult: result,
        exposurePercentage: 0,
        rawDosePercentage,
        shiftAdjustedDosePercentage,
        exposureColor: LOQ_GRAY_COLOR,
        showCensorSymbol: false,
        censorSymbol: '',
        progressWidth: 0,
        oelPosition: 95,
        isLoq: true,
        oelLabel,
        convertedTwa,
        convertedOel,
        effectiveOelValue,
        percentageLabel,
        percentageDisplay: this.formatNumber(0, '1.1-1'),
        twaDisplay: '',
        twaUnit,
        oelLineDisplay: this.buildOelLineDisplay(labels.oel, oelLabel, effectiveOelValue, oelUnit, isNoiseSample),
        loqDisplayText: labels.loqDisplay
      };
    }

    const exposurePercentage = isNoiseSample
      ? (shiftAdjustedDosePercentage ?? 0)
      : (result.poel != null ? result.poel
        : (effectiveOel ? (result.twa / effectiveOel) * 100 : 0));

    const showCensorSymbol = !isNoiseSample &&
      result.sampleCensor !== null &&
      result.sampleCensor !== undefined &&
      result.sampleCensor !== SampleCensorType.Equal &&
      result.sampleCensor !== SampleCensorType.NA;

    const progressWidth = isNoiseSample
      ? Math.min(exposurePercentage, 100)
      : this.getProgressWidth(result.twa, effectiveOel);

    const oelPosition = isNoiseSample
      ? (exposurePercentage <= 100 ? 95 : (100 / exposurePercentage) * 100)
      : this.getOelPosition(result.twa, effectiveOel);

    const twaDisplay = isNoiseSample
      ? this.formatNumber(result.twa, '1.1-1')
      : this.formatNumber(convertedTwa, '1.0-4');

    return {
      ihLabResult: result,
      exposurePercentage,
      rawDosePercentage,
      shiftAdjustedDosePercentage,
      exposureColor: this.getExposureColor(exposurePercentage, result.jurisdictionId, tenantRiskLevels, jurisdictionRiskLevelsMap),
      showCensorSymbol,
      censorSymbol: showCensorSymbol ? SampleCensorSign[result.sampleCensor] : '',
      progressWidth,
      oelPosition,
      isLoq: false,
      oelLabel,
      convertedTwa,
      convertedOel,
      effectiveOelValue,
      percentageLabel,
      percentageDisplay: this.formatNumber(exposurePercentage, '1.1-1'),
      twaDisplay,
      twaUnit,
      oelLineDisplay: this.buildOelLineDisplay(labels.oel, oelLabel, effectiveOelValue, oelUnit, isNoiseSample),
      loqDisplayText: labels.loqDisplay
    };
  }

  private buildOelLineDisplay(oelLabel: string, oelTypeLabel: string, value: number | undefined, unit: string, isNoiseSample: boolean): string {
    const formattedValue = this.formatNumber(value ?? 0, isNoiseSample ? '1.0-2' : '1.0-4');
    return `${oelLabel}${oelTypeLabel}: ${formattedValue} ${unit}`;
  }

  private getRiskLevelsForJurisdiction(
    jurisdictionId: string | undefined,
    tenantRiskLevels: RiskLevel[],
    jurisdictionRiskLevelsMap: Map<string, RiskLevel[]>
  ): RiskLevel[] {
    if (jurisdictionId != null && jurisdictionRiskLevelsMap.has(jurisdictionId)) {
      return jurisdictionRiskLevelsMap.get(jurisdictionId) || tenantRiskLevels;
    }
    return tenantRiskLevels;
  }

  private buildLegendFromRiskLevels(riskLevels: RiskLevel[]): { color: string; text: string }[] {
    return riskLevels.map((level, index) => {
      const [start, end] = level.QuantThreshold_OEL;
      let text: string;
      if (start === 0) {
        text = `<${end + 0.01}%`;
      } else if (index === riskLevels.length - 1) {
        text = `>${start}%`;
      } else {
        text = `${start}-${end}%`;
      }
      return { color: sanitizeCssColor(level.Color), text };
    });
  }

  private getEffectiveOel(result: IhLabResult): number {
    return result.correctedOEL || result.oel || 0;
  }

  private getExposureColor(
    percentage: number,
    jurisdictionId: string | undefined,
    tenantRiskLevels: RiskLevel[],
    jurisdictionRiskLevelsMap: Map<string, RiskLevel[]>
  ): string {
    return this.resolveExposureBand(percentage, jurisdictionId, tenantRiskLevels, jurisdictionRiskLevelsMap).color;
  }

  /**
   * Bands a %OEL value against the jurisdiction's (or tenant's) exposure-likelihood levels and
   * returns the matched level's name AND colour. `getExposureColor` keeps returning only the colour
   * for the cell styling that needs it, while callers that must also render the band label (e.g. the
   * MultiSample detailed-results table) read the name here rather than re-deriving the banding.
   */
  resolveExposureBand(
    percentage: number,
    jurisdictionId: string | undefined,
    tenantRiskLevels: RiskLevel[],
    jurisdictionRiskLevelsMap: Map<string, RiskLevel[]>
  ): { name: string; color: string } {
    const riskLevels = this.getRiskLevelsForJurisdiction(jurisdictionId, tenantRiskLevels, jurisdictionRiskLevelsMap);
    if (!riskLevels.length) return { name: '', color: DEFAULT_EXPOSURE_COLOR };

    let matched: RiskLevel | undefined;
    riskLevels.forEach((level, index) => {
      const [start, end] = level.QuantThreshold_OEL;
      if (percentage >= start && percentage <= end) {
        matched = level;
      }
      if (index === riskLevels.length - 1 && percentage > end) {
        matched = level;
      }
    });
    return matched
      ? { name: matched.Name ?? '', color: sanitizeCssColor(matched.Color) || DEFAULT_EXPOSURE_COLOR }
      : { name: '', color: DEFAULT_EXPOSURE_COLOR };
  }

  private getProgressWidth(twa: number, oel: number): number {
    return oel ? Math.min((twa / oel) * 100, 100) : 0;
  }

  private getOelPosition(twa: number, oel: number): number {
    if (!oel) return 95;
    return twa <= oel ? 95 : (oel / twa) * 100;
  }

  private getDuration(sample: Sample | null | undefined): string {
    if (sample?.sampleDuration) {
      const minutes = Math.round(sample.sampleDuration / 60);
      return `${minutes} min`;
    }
    return '';
  }

  private getUnitSymbol(unitValue: number | string): string {
    const unitNum = typeof unitValue === 'string' ? parseInt(unitValue, 10) : unitValue;
    return (unitNum !== undefined && unitNum !== null && !isNaN(unitNum)) ? UnitSymbols[unitNum] || '' : '';
  }

  private getOelLabel(ihLabResult: IhLabResult | null | undefined, labels: GravimetricPresentationLabels): string {
    if (!ihLabResult) return '(TWA)';
    if (ihLabResult.isOelCorrectionApplied) return labels.shiftAdjustedLabel;
    return ihLabResult.oelTypeName ? `(${ihLabResult.oelTypeName})` : '(TWA)';
  }

  private getExposureLevel(percentage: number): string {
    if (percentage >= 100) return 'high';
    if (percentage >= 75) return 'medium';
    return 'low';
  }

  private getConvertedTwa(ihLabResult: IhLabResult): number {
    if (!ihLabResult?.twa) return 0;
    return this.valueConversionService.getParameterValueAsPerAgentUnit(
      ihLabResult.twa,
      ihLabResult.agentUnits as Units
    );
  }

  private getConvertedOel(ihLabResult: IhLabResult): number {
    const effectiveOel = this.getEffectiveOel(ihLabResult);
    if (!effectiveOel) return 0;
    return this.valueConversionService.getParameterValueAsPerAgentUnit(
      effectiveOel,
      ihLabResult.agentUnits as Units
    );
  }

  private formatNumber(value: number, format: string): string {
    return this.decimalPipe.transform(value, format) ?? '';
  }
}
